import { useCallback } from "react"

type ScrollOptions = {
  offset?: number
  duration?: number
  easing?: (t: number) => number
}

/**
 * Custom hook for smooth scrolling to elements
 * @returns Function to smoothly scroll to an element by ID
 */
export default function useSmoothScroll() {
  /**
   * Ease-in-out cubic function for smoother scrolling
   */
  const easeInOutCubic = (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

  /**
   * Smoothly scrolls to the element with the given ID
   */
  const scrollToElement = useCallback(
    (
      elementId: string,
      {
        offset = 70,
        duration = 700,
        easing = easeInOutCubic,
      }: ScrollOptions = {}
    ) => {
      let targetElement = document.querySelector(
        `[id="${elementId}"]`
      ) as HTMLElement | null

      // Workaround for missing footnote back-reference IDs
      if (!targetElement && elementId.includes("fnref-")) {
        const forwardHref = `#${elementId.replace("fnref-", "fn-")}`
        const forwardLink = document.querySelector(`a[href="${forwardHref}"]`)
        if (forwardLink) {
          targetElement = forwardLink.closest("sup")
        }
      }

      if (!targetElement) {
        return
      }

      const targetPosition =
        targetElement.getBoundingClientRect().top + window.scrollY - offset
      const startPosition = window.scrollY
      const distance = targetPosition - startPosition
      let startTime: number | null = null

      const animateScroll = (currentTime: number) => {
        if (startTime === null) startTime = currentTime
        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / duration, 1)
        const easedProgress = easing(progress)

        window.scrollTo(0, startPosition + distance * easedProgress)

        if (elapsedTime < duration) {
          requestAnimationFrame(animateScroll)
        }
      }

      requestAnimationFrame(animateScroll)
    },
    []
  )

  return scrollToElement
}
